/*
 * Nybroe checkout guard. Catering plan v1.0.0 section 3.4, corrections C13 and C15.
 *
 * - Every checkout button carries data-nyb-checkout and starts disabled in the HTML. It is enabled only while
 *   the cart block publishes a valid nyb:state.
 * - A capture-phase submit listener on document catches the checkout submit (the submitter named "checkout",
 *   or any submit of form#cart, which covers Enter-key implicit submission). It never lets the submit through
 *   on page state alone: it saves pending input, reads the server cart (attributes, item count, Date header)
 *   and freshly fetched rules, and runs NybRules.validate.
 * - The rules are fetched again on every guarded click, never taken from the cart block or an earlier fetch.
 *   NybOrder.fetchConfig gives each request its own URL and cache 'no-store', because Shopify caches
 *   /cart?sections= renders: in QA on 2026-09-17 a click 15 s after ordering_paused was set true still passed
 *   on a cached render (finding F1). The clock is unchanged: server Date header of /cart.js, device as fallback.
 * - Invalid: clears the keys validate() names, shows the message in the block's alert region and moves focus
 *   there. Valid: writes Stopptid, StopptidEpoch, Validerad, Tidskalla and Regelversion, then submits again
 *   with the same submitter so the "checkout" field is posted.
 * - The button gets aria-busy while validating and is NEVER disabled then: a disabled submitter is left out of
 *   the form data, and the customer would land back on /cart (C13).
 * - Network errors fail closed.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined' || window.NybCheckoutGuard) return;

  var RESUBMIT_BLOCK_MS = 8000;
  var passNext = false;
  var inFlight = false;
  var lastValid = false;
  var submittedAt = 0;

  function checkoutButtons() {
    return document.querySelectorAll('[data-nyb-checkout]');
  }

  function applyState() {
    if (inFlight) return;
    var buttons = checkoutButtons();
    for (var i = 0; i < buttons.length; i++) buttons[i].disabled = !lastValid;
  }

  document.addEventListener('nyb:state', function (event) {
    lastValid = !!(event.detail && event.detail.valid === true);
    applyState();
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      submittedAt = 0;
      lastValid = false;
      applyState();
    }
  });

  function genericMessage() {
    var b = document.querySelector('[data-nyb-checkout][data-nyb-error]');
    return (b && b.getAttribute('data-nyb-error')) || 'Något gick fel, försök igen.';
  }

  function showMessage(message) {
    var O = window.NybOrder;
    var block = O && typeof O.cartBlock === 'function' ? O.cartBlock() : null;
    if (block) {
      block.showAlert(message, true);
      return;
    }
    var box = document.getElementById('cart-errors');
    if (box) {
      box.setAttribute('role', 'alert');
      box.setAttribute('tabindex', '-1');
      box.textContent = message;
      box.focus();
    }
  }

  function isGuarded(event) {
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') return true;
    var form = event.target;
    return !!(form && form.id === 'cart');
  }

  function finish(submitter) {
    inFlight = false;
    if (submitter) submitter.removeAttribute('aria-busy');
  }

  function submitAgain(form, submitter) {
    passNext = true;
    try {
      submitter.disabled = false;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
      else submitter.click();
      submittedAt = Date.now();
    } finally {
      /* The submit event is dispatched synchronously, so the pass has been used (or was never needed). */
      passNext = false;
    }
  }

  function validateAndSubmit(form, submitter) {
    var O = window.NybOrder;
    var R = window.NybRules;
    if (!O || !R || !submitter) {
      showMessage(genericMessage());
      return;
    }
    inFlight = true;
    submitter.setAttribute('aria-busy', 'true');

    O.flush()
      .then(function () {
        /* Fresh rules for this click (F1): a new, uncached config request every time, never the block's copy. */
        return Promise.all([O.fetchCart(), O.fetchConfig()]);
      })
      .then(function (results) {
        var cartRes = results[0];
        var config = results[1];
        var cart = cartRes.cart || {};
        var now = O.resolveNow(cartRes.dateHeader, config);

        if (!cart.item_count) {
          var emptyClear = R.emptyCartClear(cart);
          return (emptyClear ? O.save(emptyClear) : Promise.resolve()).then(function () {
            return { ok: false, message: genericMessage() };
          });
        }

        var result = R.validate(cart.attributes, now.ms, config, { ignoreStamp: true });
        if (!result.ok) {
          var clear = {};
          result.clear.forEach(function (k) { clear[k] = ''; });
          var saving = result.clear.length ? O.save(clear) : Promise.resolve();
          return saving.then(function () {
            return {
              ok: false,
              message: O.messageFor(result.errors[0], config) || genericMessage(),
              cart: Object.assign({}, cart, { attributes: Object.assign({}, cart.attributes, clear) }),
              config: config,
              dateHeader: cartRes.dateHeader
            };
          });
        }

        return O.save(R.checkoutStamp(cart.attributes, now, config)).then(function () {
          return { ok: true };
        });
      })
      .then(function (outcome) {
        finish(submitter);
        if (outcome.ok) {
          submitAgain(form, submitter);
          return;
        }
        var block = O.cartBlock();
        if (block && outcome.cart) block.applyServerCart(outcome.cart, outcome.config, outcome.dateHeader);
        showMessage(outcome.message);
      })
      .catch(function () {
        finish(submitter);
        showMessage(genericMessage());
      });
  }

  document.addEventListener(
    'submit',
    function (event) {
      if (!isGuarded(event)) return;
      if (passNext) {
        passNext = false;
        return;
      }
      event.preventDefault();
      if (inFlight) return;
      if (submittedAt && Date.now() - submittedAt < RESUBMIT_BLOCK_MS) return;
      var submitter =
        event.submitter && event.submitter.name === 'checkout' ? event.submitter : document.getElementById('checkout');
      validateAndSubmit(event.target, submitter);
    },
    true
  );

  window.NybCheckoutGuard = {
    VERSION: '1.0.0',
    get inFlight() { return inFlight; },
    get lastValid() { return lastValid; }
  };
})();
